/**
 * Drift-decision copy + impact-summary derivation for the Baseline Compliance
 * drift drawer (DriftDetailDrawer).
 *
 * The drawer serves two drift sources that behave differently on the server,
 * and the customer needs to understand that difference before they decide.
 * This module owns the *words* (and the small amount of derivation behind
 * them) as pure functions so the drawer stays declarative and the harness can
 * unit-test the branching without a DOM (component tests here never run
 * effects — see .agents memory `reference_web_component_test_harness`).
 *
 * Two source kinds, named in customer words elsewhere — internal collector
 * names (the scanned SharePoint/OneDrive baseline vs the continuously
 * monitored configuration) never appear in any string this module returns.
 *
 * #18 S2 (CA-creation detector) adds a third dimension: a watch-source row can
 * now be a *newly created* policy Guardian spotted between checks, not just a
 * change to an existing one. Those rows (`pipelineSource === 'CreationDetector'`
 * && `driftType === 'Added'`) get their own queue + drawer framing and an
 * honest lifecycle note — the detector row is sticky until the policy is either
 * deleted in Microsoft 365 or adopted into the watched set, at which point
 * Guardian closes it on its own. The helpers below own all of that copy plus
 * the customer-voiced mapping for the four fold-time server errors, so the
 * drawer/queue stay declarative and the jargon sweep is enforced in tests.
 *
 * The deletion-detector + retire-OUT wave adds the symmetric half: a watched
 * policy that has *vanished* from the tenant surfaces as a removal row
 * (`pipelineSource === 'DeletionDetector' | 'SnapshotDiff'` && `driftType ===
 * 'Removed'`). Accepting one of those retires the policy OUT of the watched
 * baseline so Guardian stops reporting it. The retire copy is gated on the
 * SOURCE, not on `driftType` alone — an ordinary `Monitor` `Removed` row is NOT
 * retire-eligible on the server (`IsRetireRow` excludes `Source==='Monitor'`),
 * so it keeps the honest/generic copy rather than promising a retire that won't
 * happen.
 */

import { format } from 'date-fns'

export type DriftSourceKind = 'scan' | 'watch'

/**
 * Which Guardian pipeline produced a continuous-watch row. Mirrors the backend
 * `TcmDriftEntity.Source` so callers can pass the wire value straight through.
 *   - 'Monitor'          = an ordinary change to a watched setting.
 *   - 'CreationDetector' = a brand-new policy Guardian found between checks
 *                          (#18 S2, `Added`) — accept folds it IN.
 *   - 'DeletionDetector' = a watched policy that vanished from the tenant
 *                          (`Removed`) — accept retires it OUT.
 *   - 'SnapshotDiff'     = a recurring config-sweep finding (#14 Tier-2); its
 *                          `Removed` rows share the retire-OUT accept leg.
 * Defaulting to 'Monitor' keeps every existing call-site unchanged when the
 * field is absent (older rows, the DSC source). Kept in lock-step with
 * `TcmDriftPipelineSource` in `lib/api.ts` (the wire-shape twin).
 */
export type DriftPipelineSource =
  | 'Monitor'
  | 'CreationDetector'
  | 'DeletionDetector'
  | 'SnapshotDiff'

/**
 * True when a watch-source row is a newly created policy the detector flagged
 * (Added) rather than a modification to an existing one. Both conditions are
 * required: a future detector-sourced Modified row would NOT take the
 * new-policy framing, mirroring the backend's `IsCreationFoldRow` predicate.
 * DSC rows are never detector rows.
 */
export function isCreationDetectorRow(input: {
  source: 'DSC' | 'TCM'
  pipelineSource?: DriftPipelineSource | null
  driftType?: string | null
}): boolean {
  return (
    input.source === 'TCM' &&
    input.pipelineSource === 'CreationDetector' &&
    (input.driftType ?? '').toLowerCase() === 'added'
  )
}

/**
 * True when a watch-source row is a *retire-eligible removal* — a policy that
 * was being watched and has now vanished from the tenant. Accepting it retires
 * the policy OUT of the watched baseline so Guardian stops reporting it.
 *
 * Gated on the SOURCE deliberately, NOT on `driftType==='Removed'` alone: the
 * backend's `IsRetireRow` only retires `Source ∈ {DeletionDetector,
 * SnapshotDiff}` removals. An ordinary `Monitor` `Removed` row is NOT
 * retire-eligible there, so it must keep the honest/generic copy rather than
 * promise a retire the accept won't perform. Both the detector pass and the
 * #14 recurring config sweep can author a retire-eligible removal, so both
 * sources qualify. DSC rows are never retire rows.
 */
export function isDeletionDetectorRow(input: {
  source: 'DSC' | 'TCM'
  pipelineSource?: DriftPipelineSource | null
  driftType?: string | null
}): boolean {
  return (
    input.source === 'TCM' &&
    (input.pipelineSource === 'DeletionDetector' ||
      input.pipelineSource === 'SnapshotDiff') &&
    (input.driftType ?? '').toLowerCase() === 'removed'
  )
}

/**
 * Map the drawer's internal source flag to the source *kind* this module
 * reasons about. Kept as a tiny indirection so callers pass their existing
 * `'DSC' | 'TCM'` flag and we own the customer-facing vocabulary in one place
 * — no collector name leaks past this boundary.
 *
 * - `'DSC'`  → `'scan'`  (the SharePoint & OneDrive baseline scan)
 * - `'TCM'`  → `'watch'` (Guardian's continuous configuration watch)
 */
export function driftSourceKind(source: 'DSC' | 'TCM'): DriftSourceKind {
  return source === 'TCM' ? 'watch' : 'scan'
}

/**
 * Which channel found a drift row, for the queue's per-row tier marker and its
 * "Found by" filter. Finer than `driftSourceKind`: the continuous-watch source
 * splits into its two tiers (terminology decision #9, Adam 2026-09-23).
 *   - 'watch'   = Continuous watch: the 6h monitor and its between-checks
 *                 detectors (Source Monitor / CreationDetector / DeletionDetector).
 *   - 'capture' = Full capture: the recurring config sweep (Source SnapshotDiff).
 *   - 'scan'    = the SharePoint & OneDrive baseline scan (every DSC row).
 * A watch row with no pipeline source (older rows) reads as 'watch', the same
 * default `DriftPipelineSource` documents.
 */
export type DriftTier = 'watch' | 'capture' | 'scan'

export function driftTier(source: 'DSC' | 'TCM', pipelineSource?: DriftPipelineSource | null): DriftTier {
  if (source === 'DSC') return 'scan'
  return pipelineSource === 'SnapshotDiff' ? 'capture' : 'watch'
}

/** The tier names, as the lane counts above the queue already print them. */
export const DRIFT_TIER_LABEL: Record<DriftTier, string> = {
  watch: 'Continuous watch',
  capture: 'Full capture',
  scan: 'SharePoint & OneDrive',
}

/**
 * Quiet provenance line for the drawer's identity section — "how Guardian saw
 * this". Customer words only; the detected timestamp is rendered separately by
 * the drawer (it already has the formatter + the value), so this is just the
 * sentence that sits beside it.
 *
 * A creation-detector row gets a more specific sentence: Guardian watches for
 * brand-new policies between its scheduled checks, so the provenance names that
 * "between-checks watch" honestly rather than implying the policy was caught by
 * an ordinary configuration check. A removal (deletion-detector) row names the
 * same between-checks watch but for the inverse event — a watched policy that
 * went missing. The optional second argument carries the detector dimension;
 * omit it and the label is the existing per-source copy.
 *
 * A Tier-2 recurring config-sweep row (`pipelineSource === 'SnapshotDiff'`)
 * gets its own provenance ahead of the detector checks below — the sweep is a
 * separate tier from the Tier-1 continuous watch (Adam, 2026-09-23; see
 * terminology-decisions-2026-09-22.md), so it never shares "continuous watch"
 * wording. This covers every SnapshotDiff `driftType`, including `Removed`,
 * which would otherwise fall into the deletion-detector's between-checks-watch
 * line below.
 */
export function provenanceLabel(
  source: 'DSC' | 'TCM',
  opts?: { pipelineSource?: DriftPipelineSource | null; driftType?: string | null },
): string {
  if (source === 'TCM' && opts?.pipelineSource === 'SnapshotDiff') {
    return "Seen by Guardian's full-configuration capture"
  }
  if (isCreationDetectorRow({ source, ...opts })) {
    return "Spotted by Guardian's between-checks watch for new policies"
  }
  if (isDeletionDetectorRow({ source, ...opts })) {
    return "Spotted by Guardian's between-checks watch for removed policies"
  }
  return driftSourceKind(source) === 'watch'
    ? "Seen by Guardian's continuous watch"
    : "Seen by Guardian's SharePoint & OneDrive baseline scan"
}

/**
 * The one-sentence truth shown when a customer is about to reject a drift.
 * Verified server behaviour, surfaced honestly so the reject isn't a surprise:
 *
 *  - scan  (DSC): rejection records the decision, but the next scan re-flags
 *                 the change as new if it's still present in the tenant.
 *  - watch (TCM): rejection is sticky — Guardian won't flag this exact change
 *                 again; only restoring the setting in Microsoft 365 closes the
 *                 loop, which Guardian then notices on its own.
 *  - detector (TCM, new policy): rejecting a brand-new policy does NOT make the
 *                 row go away — it stays on the list while the policy is live,
 *                 because an unreviewed new policy is exactly what the customer
 *                 should keep seeing. Guardian closes it on its own once the
 *                 policy is removed in Microsoft 365 or added to the watched
 *                 set. This mirrors §3's lifecycle (sticky until deleted or
 *                 adopted) — the reject simply records the decision-so-far.
 *  - removal (TCM, retire row): rejecting a removed policy keeps it on the list.
 *                 The policy is already gone from Microsoft 365 — rejecting just
 *                 records that you want Guardian to keep reporting it rather than
 *                 retiring it from the watched set. Guardian closes it on its own
 *                 if the policy comes back, or once you accept the retire.
 *
 * The optional second argument carries the detector dimension; omit it and the
 * copy is the existing per-source behaviour.
 */
export function rejectConsequenceCopy(
  source: 'DSC' | 'TCM',
  opts?: { pipelineSource?: DriftPipelineSource | null; driftType?: string | null },
): string {
  if (isCreationDetectorRow({ source, ...opts })) {
    return 'Rejecting records that you have looked at this new policy. It stays on your list while it is live and not yet part of the settings Guardian watches. Guardian clears it on its own once the policy is removed in Microsoft 365, or once you add it to those settings.'
  }
  if (isDeletionDetectorRow({ source, ...opts })) {
    return 'Rejecting keeps this removed policy on your list. The policy is already gone from Microsoft 365. Rejecting tells Guardian to keep reporting it rather than retire it from the settings it watches. Guardian clears it on its own if the policy comes back, or once you accept the retire.'
  }
  return driftSourceKind(source) === 'watch'
    ? 'Rejecting records your decision and stops Guardian from flagging this exact change again. To bring the setting back in line, restore it in Microsoft 365. Guardian closes the loop on its own.'
    : 'Rejecting records your decision. If the change is still there on the next scan, it returns to the queue as new.'
}

/* ────────────────────────────────────────────────────────────────────
   Continuous-watch (TCM) approval impact.

   The watch's approval-impact preview carries NO sibling-drift cascade —
   the server returns the single drift's own resource type and a flag that
   accepting forks a new baseline version (it always does today). So there
   is no honest "this also covers N other changes" warning to show; forcing
   one would fabricate a number. What we CAN say truthfully is which baseline
   version the accept forks from, when the preview gives us a real one.

   `deriveWatchAcceptImpact` distills the raw preview into the only two facts
   the drawer renders: that a new baseline version is created, and the
   version it forks from (when known). The copy lives here so the warning
   text is unit-tested and the drawer stays declarative.
   ──────────────────────────────────────────────────────────────────── */

/**
 * Slim view of the watch's approval-impact preview — only the fields that
 * change what the drawer says. Mirrors `TcmDriftApprovalImpact` in
 * `@/types/tcm` but kept structural so the helper stays test-friendly and
 * doesn't couple the pure module to the wire type.
 */
export interface WatchAcceptImpactInput {
  /** Server flag — true means accepting forks a new baseline version. */
  willCreateMonitorVersion: boolean
  /** The baseline version the accept forks from. 0 / undefined when unknown. */
  currentVersion?: number | null
}

export interface WatchAcceptImpact {
  /** Whether to surface the re-baseline notice at all. */
  createsNewBaselineVersion: boolean
  /**
   * The version the accept forks from, when the preview gave us a real one
   * (> 0). `null` keeps the drawer copy generic instead of printing
   * "version 0", which would read as a fabricated/placeholder number.
   */
  forksFromVersion: number | null
  /**
   * Pre-composed sentence the drawer drops beside the Accept action when
   * `createsNewBaselineVersion` is true. Names the fork-from version only
   * when known; otherwise stays version-agnostic. Never invents a count of
   * other affected changes — the preview doesn't carry one.
   */
  summary: string | null
}

/**
 * Distill the raw watch approval-impact preview into the drawer's render
 * inputs. Returns `createsNewBaselineVersion: false` (and a null summary)
 * when the preview indicates the accept won't fork a version — in which case
 * the drawer shows nothing extra, matching the "render nothing rather than
 * fabricate" rule.
 */
export function deriveWatchAcceptImpact(
  impact: WatchAcceptImpactInput | null | undefined,
): WatchAcceptImpact {
  if (!impact || !impact.willCreateMonitorVersion) {
    return {
      createsNewBaselineVersion: false,
      forksFromVersion: null,
      summary: null,
    }
  }

  // Treat any non-positive / missing version as "unknown" — version 0 is the
  // server's null-tolerant default when no active monitor exists yet, not a
  // real version to name to the customer.
  const v = typeof impact.currentVersion === 'number' ? impact.currentVersion : 0
  const forksFromVersion = v > 0 ? v : null

  const summary =
    forksFromVersion !== null
      ? `Accepting forks a fresh baseline from version ${forksFromVersion}. This change stops reading as drift from then on.`
      : 'Accepting forks a fresh baseline version. This change stops reading as drift from then on.'

  return {
    createsNewBaselineVersion: true,
    forksFromVersion,
    summary,
  }
}

/* ────────────────────────────────────────────────────────────────────
   Creation-detector copy (#18 S2).

   A detector row is a brand-new policy Guardian spotted between checks —
   it reads "New policy detected: <name>" rather than "<setting> changed".
   These helpers own every customer-facing word: the queue line, the drawer
   headline + lead paragraph, the Accept framing ("add it to the watched
   set"), and the lifecycle note. All of it stays jargon-free — no UTCM /
   monitor / snapshot / baseline-internals language; "baseline" appears only
   where it's already shipped customer vocabulary (the watched approved set).
   ──────────────────────────────────────────────────────────────────── */

export interface CreationDetectorCopy {
  /** Eyebrow kicker for the queue row + drawer ("New policy"). */
  kicker: string
  /**
   * One-line queue/drawer title: `New policy detected: <name>`. Falls back to
   * a name-agnostic phrasing when the live displayName is missing, rather than
   * printing an empty colon.
   */
  title: string
  /**
   * Lead paragraph for the drawer — what Guardian found and why it's on the
   * list. Names the subject (the policy), not a row count.
   */
  lead: string
  /** Label for the Accept action — frames the accept as adoption, not a fix. */
  acceptLabel: string
  /**
   * Sentence beside the Accept action: accepting adds the policy to the
   * settings Guardian watches (the watched baseline). Customer voice only.
   */
  acceptNote: string
}

/**
 * Compose the creation-detector copy for one new-policy row. `policyName` is
 * the live displayName Guardian read from Microsoft 365; when it's blank we
 * keep the framing but drop the name so the title never trails a bare colon.
 */
export function creationDetectorCopy(policyName: string | null | undefined): CreationDetectorCopy {
  const name = policyName?.trim()
  const title = name ? `New policy detected: ${name}` : 'New policy detected'
  const lead = name
    ? `Guardian spotted a new policy, ${name}, that isn't part of the settings it watches yet. Review it before it becomes part of normal. Accept it to fold it into your watched baseline, or leave it on the list while you decide.`
    : "Guardian spotted a new policy that isn't part of the settings it watches yet. Review it before it becomes part of normal. Accept it to fold it into your watched baseline, or leave it on the list while you decide."
  return {
    kicker: 'New policy',
    title,
    lead,
    acceptLabel: 'Add to watched baseline',
    acceptNote:
      'Accepting adds this policy to the settings Guardian watches, and it stops showing as new. Guardian then flags any later change to it, the same as everything else in your baseline.',
  }
}

/* ────────────────────────────────────────────────────────────────────
   Deletion-detector copy (retire-OUT — the symmetric half of #18 S2).

   A removal row is a policy Guardian was watching that has vanished from the
   tenant. It reads "Removed policy: <name>" rather than "<setting> changed".
   These helpers own every customer-facing word for the retire affordance: the
   queue kicker, the verdict headline, the lead, the Accept framing ("retire it
   from the watched set"), and the honest body. All of it stays jargon-free —
   no UTCM / monitor / snapshot / baseline-internals language; "baseline"
   appears only where it's already shipped customer vocabulary (the watched
   approved set). The verdict headline is ≤6 words, named-subject first, per
   Product Voice — and carries NO em-dash (the period-then-sentence reads
   cleaner and dodges the AI-coded em-dash tell on a headline surface).
   ──────────────────────────────────────────────────────────────────── */

export interface DeletionDetectorCopy {
  /** Eyebrow kicker for the queue row + drawer ("Removed policy"). */
  kicker: string
  /**
   * Verdict headline, six words or fewer, named-subject first, no em-dash.
   * "This policy is gone. Retire it." — names the verdict before the data, per
   * the score-box pattern. The drawer/queue render it where #18 renders the
   * creation `title`.
   */
  verdict: string
  /**
   * One-line queue/drawer title: `Removed policy: <name>`. Falls back to a
   * name-agnostic phrasing when the live displayName is missing, rather than
   * printing an empty colon.
   */
  title: string
  /**
   * Lead paragraph for the drawer — what Guardian found and why it's on the
   * list. Names the subject (the policy), not a row count.
   */
  lead: string
  /** Label for the Accept action — frames the accept as a retire, not a fix. */
  acceptLabel: string
  /**
   * Sentence beside the Accept/Reject decision: accepting retires the policy
   * out of the watched set so Guardian stops reporting it; rejecting keeps it
   * on the list. Customer voice only. This is the honest "what each button
   * does" body the spec pins.
   */
  body: string
}

/**
 * Compose the deletion-detector (retire-OUT) copy for one removed-policy row.
 * `policyName` is the displayName Guardian last knew the policy by; when it's
 * blank we keep the framing but drop the name so the title never trails a bare
 * colon. The `verdict` is the ≤6-word headline; `body` is the honest
 * accept-vs-reject sentence the decision surface renders.
 */
export function deletionDetectorCopy(policyName: string | null | undefined): DeletionDetectorCopy {
  const name = policyName?.trim()
  const title = name ? `Removed policy: ${name}` : 'Removed policy'
  const lead = name
    ? `Guardian was watching a policy, ${name}, that is no longer in Microsoft 365. A removed security control is worth a deliberate sign-off before Guardian stops tracking it. Accept it to retire it from the settings Guardian watches, or leave it on the list while you decide.`
    : 'Guardian was watching a policy that is no longer in Microsoft 365. A removed security control is worth a deliberate sign-off before Guardian stops tracking it. Accept it to retire it from the settings Guardian watches, or leave it on the list while you decide.'
  return {
    kicker: 'Removed policy',
    verdict: 'This policy is gone. Retire it.',
    title,
    lead,
    acceptLabel: 'Retire from watched baseline',
    body: 'Accepting folds this removal into your watched baseline, so Guardian stops reporting it. Rejecting keeps it on your list.',
  }
}

/* ────────────────────────────────────────────────────────────────────
   Acknowledgeable recurring config-sweep findings (TCM SnapshotDiff).

   SnapshotDiff rows with Added or Modified drift are recurring config-sweep
   findings. The server accepts these through the existing stage → apply
   flow, flipping them to Accepted with no baseline write. Gate this on
   SOURCE, never on driftType alone, so it cannot collide with the
   SnapshotDiff + Removed retire-eligible case handled by isDeletionDetectorRow.
   ──────────────────────────────────────────────────────────────────── */

/**
 * True only for a TCM SnapshotDiff row whose drift type is Added or Modified.
 * SOURCE must match. Never infer acknowledge behavior from driftType alone.
 */
export function isAcknowledgeRow(input: {
  source: 'DSC' | 'TCM'
  pipelineSource?: DriftPipelineSource | null
  driftType?: string | null
}): boolean {
  return (
    input.source === 'TCM' &&
    input.pipelineSource === 'SnapshotDiff' &&
    ['added', 'modified'].includes((input.driftType ?? '').toLowerCase())
  )
}

/** Customer-safe copy for a full-capture (Tier-2 sweep) finding that can be
 *  acknowledged as the new normal. Acknowledging never writes the watched
 *  baseline, so the setting is not added to Continuous watch. */
export interface AcknowledgeCopy {
  acceptLabel: string
  modalTitle: string
  body: string
  note: string
}

export function acknowledgeCopy(): AcknowledgeCopy {
  return {
    acceptLabel: 'Acknowledge change',
    modalTitle: 'Acknowledge this change',
    body: 'Acknowledging records this change as expected, and Full capture treats it as the new normal from the next capture. It does not add the setting to Continuous watch. Rejecting records that it should be reverted.',
    note: 'Acknowledging makes this the new normal. It does not add the setting to Continuous watch.',
  }
}

/* ────────────────────────────────────────────────────────────────────
   Upgrade-introduced settings (DSC SharePoint & OneDrive).

   When Guardian's SharePoint/OneDrive check starts reporting a property the
   baseline never captured (the baseline was taken by an older check that
   didn't emit it), the change arrives with no "previous" value — `oldValue`
   normalises to "Not Present"/Absent/null. On a Hybrid tenant DSC owns only
   SPO + OneDrive, so these new-setting rows cluster there. They aren't a
   configuration *change* the customer made; they're new surface area. We tag
   them so the queue can tell them apart from a real change and the customer
   can bulk-accept them to fold them into the baseline.

   This is the scan-source twin of the creation-detector (watch-source) "new
   policy" framing. Gated on SOURCE ('DSC'), the SPO/ODB component predicate
   (mirrors the backend `Wave9Scope.IsSpoOdbComponent`), AND a not-present
   `oldValue` so a real Present→Absent removal (e.g. SPOHomeSite dropping out)
   keeps its ordinary drift framing rather than reading as a new setting.
   ──────────────────────────────────────────────────────────────────── */

/**
 * Mirror of the backend `Wave9Scope.IsSpoOdbComponent` predicate: a DSC
 * component is SharePoint/OneDrive when its name starts with "SPO" or is
 * exactly "ODSettings" (case-insensitive). Single source of truth on the
 * server; this FE copy stays in lock-step so the "new setting" tag never
 * fires on a non-SPO/ODB component.
 */
export function isSpoOdbComponent(component: string | null | undefined): boolean {
  const name = (component ?? '').trim().toLowerCase()
  if (!name) return false
  return name.startsWith('spo') || name === 'odsettings'
}

/**
 * True when a DSC drift's `oldValue` means "the baseline had no value here" —
 * i.e. the property is brand-new to the baseline. Mirrors the set the backend
 * `NormalizeValueForComparison` collapses to null: empty, "Not Present",
 * "Absent", "$null"/"null", "{}", "[]". A genuine prior value (e.g. "Present",
 * "True") is NOT not-present, so a real change keeps its ordinary framing.
 */
const NOT_PRESENT_OLD_VALUES = new Set([
  '',
  'not present',
  'absent',
  '$null',
  'null',
  '{}',
  '[]',
])
export function isNotPresentOldValue(value: string | null | undefined): boolean {
  if (value == null) return true
  return NOT_PRESENT_OLD_VALUES.has(value.trim().toLowerCase())
}

/**
 * True when a row is a setting a newer SharePoint/OneDrive check began
 * reporting that the baseline never had — a "new setting", not a change.
 * Requires all three: the scan source ('DSC'), an SPO/ODB component, and a
 * not-present `oldValue`. DSC rows on a Hybrid tenant are SPO/ODB-only by
 * construction, but the component check keeps the tag honest regardless of
 * where it's called. TCM rows are never upgrade rows (the watch source has
 * its own creation/deletion detectors).
 */
export function isUpgradeIntroducedRow(input: {
  source: 'DSC' | 'TCM'
  component?: string | null
  oldValue?: string | null
}): boolean {
  return (
    input.source === 'DSC' &&
    isSpoOdbComponent(input.component) &&
    isNotPresentOldValue(input.oldValue)
  )
}

export interface UpgradeIntroducedCopy {
  /** Eyebrow kicker for the queue row + drawer ("New setting"). */
  kicker: string
  /**
   * Lead paragraph for the drawer — what Guardian found and why it's on the
   * list. Names the subject (the setting), never a row count, no jargon.
   */
  lead: string
  /**
   * Sentence beside the Accept action: accepting folds the setting into the
   * baseline at its current value so it stops reading as drift. Customer
   * voice only.
   */
  acceptNote: string
}

/**
 * Compose the upgrade-introduced (new-setting) copy. No subject name is
 * threaded through — unlike a policy, a setting is identified by the
 * component/property the row already renders, so the copy stays generic and
 * the table cell carries the specifics.
 */
export function upgradeIntroducedCopy(): UpgradeIntroducedCopy {
  return {
    kicker: 'New setting',
    lead: "Guardian's SharePoint & OneDrive check started reporting this setting recently. It wasn't part of your baseline before, so it shows here as new rather than as a change you made. Settings like this are worth a glance, then accept it to fold it into your baseline so it stops showing as drift.",
    acceptNote:
      'Accepting adds this setting to your baseline at its current value. It stops showing as new, and Guardian flags any later change to it the same as everything else in your baseline.',
  }
}

/* ────────────────────────────────────────────────────────────────────
   Fold-time server-error copy (#18 S2 §4).

   Accepting a new policy can fail at the server for four honest reasons.
   The backend sends each as an ASP.NET ProblemDetails with a fixed `title`
   (the exact strings below) and an operator-voiced `detail`. We must NEVER
   render that raw `detail` to a customer — it carries capacity / capture /
   monitor language. Instead we map the title to advisor-voice copy here. Any
   title we don't recognise falls through to the caller's own fallback (via
   the returned `null`), so a future server message can't surface raw.
   ──────────────────────────────────────────────────────────────────── */

/** The four pinned fold ProblemDetails titles — exact wire strings. */
export const FOLD_PROBLEM_TITLES = {
  budget: 'Monitoring capacity reached',
  quota: 'Monthly capture allowance used up',
  capturing: "Capturing the new policy's configuration",
  gone: 'New policy no longer found',
} as const

export interface FoldErrorCopy {
  /** Verdict-voice headline, six words or fewer. */
  headline: string
  /** One or two plain sentences: what happened + what to do next. */
  body: string
}

/**
 * Customer copy for each of the four fold titles. Honest about the
 * consequence and the next step, with zero internal jargon — the operator's
 * raw `detail` never reaches the customer.
 */
const FOLD_ERROR_COPY: Record<string, FoldErrorCopy> = {
  [FOLD_PROBLEM_TITLES.budget]: {
    headline: "Your watch list is full.",
    body:
      'Adding this policy would push past the number of settings Guardian can watch for your tenant. ' +
      'Trim a few settings from what Guardian watches, then add this one. Your Guardian team can help you choose.',
  },
  [FOLD_PROBLEM_TITLES.quota]: {
    headline: "We've reached this month's limit.",
    body:
      'Guardian can take a fresh reading of a new policy a set number of times each month, and that allowance is used up for now. ' +
      "It resets at the start of next month. Leave this policy on your list and accept it then, or reach out to your Guardian team if it can't wait.",
  },
  [FOLD_PROBLEM_TITLES.capturing]: {
    headline: "Reading the new policy now.",
    body:
      "Guardian is taking a full reading of this policy so it can fold it in accurately. This usually takes a minute or two. " +
      'Try accepting again shortly. Guardian picks up where it left off, so nothing is wasted.',
  },
  [FOLD_PROBLEM_TITLES.gone]: {
    headline: "That policy is no longer there.",
    body:
      "Guardian couldn't find this policy in Microsoft 365 anymore. It looks like it was removed or renamed since it was spotted. " +
      'Undo the staged item; Guardian clears it from your list on its own if it really is gone.',
  },
}

/**
 * Map a thrown apply/accept error to customer fold copy when it carries one of
 * the four pinned fold titles; otherwise return `null` so the caller uses its
 * own fallback. Reads the ProblemDetails `title` defensively from an Axios-
 * shaped error without importing axios (keeps this module dependency-free and
 * test-friendly). The raw `detail` is deliberately ignored.
 */
export function foldErrorCopyFromError(err: unknown): FoldErrorCopy | null {
  const title = extractProblemTitle(err)
  if (!title) return null
  return FOLD_ERROR_COPY[title] ?? null
}

/**
 * Map a known fold title string directly to its copy (for callers that already
 * have the title in hand, e.g. tests or a structured error channel). Unknown
 * titles return `null`.
 */
export function foldErrorCopyForTitle(title: string | null | undefined): FoldErrorCopy | null {
  if (!title) return null
  return FOLD_ERROR_COPY[title] ?? null
}

/**
 * Pull the ProblemDetails `title` out of an unknown thrown value. Handles the
 * Axios error shape (`err.response.data.title`) Guardian's Function App proxy
 * forwards verbatim from the Container, and a bare ProblemDetails object. No
 * axios import — structural reads only.
 */
function extractProblemTitle(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null
  // Axios error: title lives on response.data.title.
  const response = (err as { response?: { data?: unknown } }).response
  const fromResponse = readTitle(response?.data)
  if (fromResponse) return fromResponse
  // Bare ProblemDetails object passed straight in.
  return readTitle(err)
}

function readTitle(data: unknown): string | null {
  if (data && typeof data === 'object') {
    const t = (data as { title?: unknown }).title
    if (typeof t === 'string' && t.trim().length > 0) return t
  }
  return null
}

/* ────────────────────────────────────────────────────────────────────
   Drift Triage Copilot (WO-3 phase 1.5) — suggestion copy.

   Guardian's triage pass writes a *suggestion* onto open drift rows (it
   never disposes anything itself). The suggestion is advisory: the human
   still approves every disposition. This block owns every customer-facing
   word the suggestion surfaces — the row pill, its hover title, the drawer
   line — plus the pure helpers the queue uses to decide when a bulk
   "accept the suggestion" action is even offered.

   Voice rules (same jargon sweep the tests enforce on this whole module):
   the suggestion phrases never name the internal machinery — no
   "phantom" / "schema" / "monitor" / "TCM" / "DSC". A suggestion is a read,
   not a verdict, so the pills open with "Likely …". `RealChange` and a null
   suggestion are the silent default — no pill, no line. Low-confidence rows
   arrive from the server already stamped as no suggestion, so nothing here
   has to threshold; a row either carries a surfaced suggestion or it does
   not. No em-dashes in any authored string (period-then-sentence reads
   cleaner and dodges the AI-coded tell).
   ──────────────────────────────────────────────────────────────────── */

/**
 * The four suggestion values the backend `TriageSuggestion` column can hold.
 * `RealChange` is the default (genuine posture change) and renders as no
 * pill; the other three are advisory reads a human approves.
 */
export type TriageSuggestion =
  | 'SchemaPhantom'
  | 'RenderingNoise'
  | 'ExpectedChurn'
  | 'RealChange'

/** Who authored the suggestion — a deterministic rule or Guardian's AI. */
export type TriageSource = 'Rule' | 'AI'

/**
 * The five triage columns as they ride the drift list/detail DTOs. All
 * optional + nullable: an un-triaged row (or a row from a build before the
 * columns shipped) simply omits them, and callers treat that as "no
 * suggestion". Wire values are loosely typed (`string | null`) to match the
 * rest of this module's tolerance for pre-enrichment rows.
 */
export interface DriftTriageFields {
  triageSuggestion?: TriageSuggestion | string | null
  triageConfidence?: number | null
  triageReason?: string | null
  triageSource?: TriageSource | string | null
  triagedAt?: string | null
}

/**
 * Customer-facing pill label per surfaceable suggestion. Advisor voice, no
 * internal jargon. `RealChange` is deliberately absent — silence is its copy.
 * The map is forward-compatible: SchemaPhantom / RenderingNoise carry copy
 * even though Phase 1 only ever produces ExpectedChurn, so Phase 2 lights
 * them up without a copy change.
 */
const TRIAGE_PILL_COPY: Record<
  'SchemaPhantom' | 'RenderingNoise' | 'ExpectedChurn',
  string
> = {
  ExpectedChurn: 'Likely routine churn',
  SchemaPhantom: 'Likely a tooling artifact',
  RenderingNoise: 'Likely display-only',
}

/**
 * Provenance sentence for a suggestion. Honest about rule-vs-AI so the
 * reader knows whether a deterministic rule or Guardian's judgement is
 * talking. Returns '' when the source is unknown so callers can omit the
 * clause rather than print a half-sentence.
 */
export function triageProvenanceLabel(source?: string | null): string {
  if (source === 'AI') return 'Suggested by Guardian AI'
  if (source === 'Rule') return 'Suggested by a Guardian rule'
  return ''
}

/** True for the three suggestion values that surface a pill. */
export function isSurfaceableSuggestion(
  suggestion?: string | null,
): suggestion is 'SchemaPhantom' | 'RenderingNoise' | 'ExpectedChurn' {
  return (
    suggestion === 'SchemaPhantom' ||
    suggestion === 'RenderingNoise' ||
    suggestion === 'ExpectedChurn'
  )
}

export interface TriageSuggestionCopy {
  /** The surfaceable suggestion this copy is for. */
  suggestion: 'SchemaPhantom' | 'RenderingNoise' | 'ExpectedChurn'
  /** Short pill label, advisor voice ("Likely routine churn"). */
  pill: string
  /** Rule-vs-AI provenance sentence, or '' when the source is unknown. */
  provenance: string
  /**
   * Hover/`title` text for the pill: the read, its provenance, and Guardian's
   * confidence when the server gave us one. Authored words only — the
   * server-authored reason is NOT folded in here (it can carry its own
   * phrasing; the drawer line owns surfacing it).
   */
  title: string
  /**
   * One line for the drawer's "Guardian's read" section: the read, the
   * server-authored reason (verbatim — the human owns the final wording),
   * and the provenance. The reason is passed through as-is, so callers that
   * jargon-sweep authored copy should sweep `pill` / `provenance` / `title`,
   * not this line.
   */
  drawerLine: string
}

function lowerFirst(s: string): string {
  return s.length === 0 ? s : s[0].toLowerCase() + s.slice(1)
}

/**
 * Compose the suggestion copy for one row, or `null` when the row carries no
 * surfaceable suggestion (`RealChange`, null, or absent). `null` is the
 * signal to render nothing — silence is the default, per §0 of the blueprint.
 */
export function triageSuggestionCopy(
  fields: DriftTriageFields,
): TriageSuggestionCopy | null {
  const suggestion = fields.triageSuggestion
  if (!isSurfaceableSuggestion(suggestion)) return null

  const pill = TRIAGE_PILL_COPY[suggestion]
  const provenance = triageProvenanceLabel(fields.triageSource)

  const confidencePart =
    typeof fields.triageConfidence === 'number' && fields.triageConfidence > 0
      ? `Guardian's confidence: ${Math.round(fields.triageConfidence)}%.`
      : ''
  const title = [`${pill}.`, provenance ? `${provenance}.` : '', confidencePart]
    .filter(Boolean)
    .join(' ')

  const reason = fields.triageReason?.trim()
  const reasonPart = reason ? ` ${reason}` : ''
  const provenancePart = provenance ? ` ${provenance}.` : ''
  const drawerLine = `Guardian's read: ${lowerFirst(pill)}.${reasonPart}${provenancePart}`

  return { suggestion, pill, provenance, title, drawerLine }
}

/**
 * The single suggestion shared by a set of selected rows, or `null` when the
 * set is empty, contains a non-surfaceable row (`RealChange` / null), or
 * mixes more than one suggestion. This is the gate for the queue's bulk
 * "Accept Guardian's suggestion" action: it appears only when every selected
 * row agrees on one surfaceable suggestion, so a bulk accept can never fan a
 * disposition across rows Guardian read differently.
 */
export function sharedTriageSuggestion(
  suggestions: Array<string | null | undefined>,
): 'SchemaPhantom' | 'RenderingNoise' | 'ExpectedChurn' | null {
  if (suggestions.length === 0) return null
  const first = suggestions[0]
  if (!isSurfaceableSuggestion(first)) return null
  return suggestions.every(s => s === first) ? first : null
}

// ── Actor lookup ("who made the change") ────────────────────────────────────

/** Wire fields the Entra audit-log lookup stamps on a watch-source drift row. */
export interface DriftActorFields {
  actorLookupStatus?: string | null
  changedBy?: string | null
  changedByUpn?: string | null
  changedByKind?: string | null
  changedAt?: string | null
  otherActorCount?: number | null
}

/** The one "who made the change" line, or null (renders nothing) unless the lookup found it. */
export function actorLineCopy(f: DriftActorFields): { text: string; hover: string | null } | null {
  if (f.actorLookupStatus !== 'Found') return null
  const when = f.changedAt ? new Date(f.changedAt) : null
  if (!when || Number.isNaN(when.getTime())) return null
  const name = f.changedBy?.trim()
  if (!name) return null
  const others = f.otherActorCount && f.otherActorCount > 0 ? f.otherActorCount : 0
  const date = `${format(when, 'MMM d')} at ${format(when, 'HH:mm')}`
  const tail =
    others === 0
      ? '.'
      : others === 1
        ? ', and by 1 other since the last check.'
        : `, and by ${others} others since the last check.`
  return {
    text: `Changed by ${name} on ${date}${tail}`,
    hover: f.changedByKind === 'User' && f.changedByUpn ? f.changedByUpn : null,
  }
}
